import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { usePanelState as useBasePanelState } from '../../../shared/components/panel';

export type UserPanelType = 'invite' | 'help' | 'pending-invites';

/**
 * Users-specific version of usePanelState
 * Extends the base panel state for user management panels
 *
 * @example
 * ```tsx
 * const { isOpen, type, id, openPanel, closePanel } = useUserPanelState();
 *
 * // Open invite user panel
 * openPanel('invite', 'new');
 *
 * // Open help panel
 * openPanel('help', 'users');
 * ```
 */
export function useUserPanelState() {
  const base = useBasePanelState();
  const [searchParams] = useSearchParams();

  // Get mode parameter if present (e.g., for invite mode=new)
  const mode = searchParams.get('mode');

  // Type-safe panel opener
  const openPanel = useCallback(
    (
      type: UserPanelType,
      id: string,
      additionalParams?: Record<string, string>
    ) => {
      base.openPanel(type, id, additionalParams);
    },
    [base]
  );

  return {
    ...base,
    mode,
    openPanel,
  };
}
